import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, MailPlus, ClipboardList, Inbox, Users, Paperclip, Send, Eye, Lightbulb, CheckCircle, RefreshCw, MailX, User, XCircle, Trash2, Plus } from 'lucide-react';
import './EmailCenter.css';

function EmailCenter({ isEshopOwner = false, manualOnly = false, isCommercial = false }) {
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [needsConfig, setNeedsConfig] = useState(false);
  const [manualEmails, setManualEmails] = useState([]);
  const [manualEmailInput, setManualEmailInput] = useState('');
  const [activeView, setActiveView] = useState('compose'); // 'compose' ou 'history'
  const [emailHistory, setEmailHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Charger la liste des clients au montage du composant (sauf si manualOnly sans isCommercial)
  useEffect(() => {
    if (!manualOnly || isCommercial) {
      loadClients();
    }
  }, [manualOnly, isCommercial]);

  // Charger l'historique quand on passe à la vue historique
  useEffect(() => {
    if (activeView === 'history') {
      loadEmailHistory();
    }
  }, [activeView]);

  const loadClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = isCommercial ? '/api/my-clients-with-email' : (isEshopOwner ? '/api/eshop/clients-with-email' : '/api/admin/clients-with-email');
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    }
  };

  const loadEmailHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Les agents et e-shop owners voient uniquement leurs propres emails
      // Les admins voient tous les emails
      let endpoint;
      if (manualOnly || isCommercial) {
        endpoint = '/api/agent/email-history';
      } else if (isEshopOwner) {
        endpoint = '/api/eshop/email-history';
      } else {
        endpoint = '/api/admin/email-history';
      }
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailHistory(response.data.emails || []);
      setHistoryTotal(response.data.total || 0);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClientSelect = (recipientId) => {
    if (selectedClients.includes(recipientId)) {
      setSelectedClients(selectedClients.filter(id => id !== recipientId));
    } else {
      setSelectedClients([...selectedClients, recipientId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      // Utiliser le format "type-id" pour chaque destinataire
      setSelectedClients(filteredClients.map(c => `${c.type}-${c.id}`));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + attachments.length > 10) {
      alert('Maximum 10 fichiers autorisés');
      return;
    }
    setAttachments([...attachments, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  // Valider le format email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Ajouter un email manuel
  const addManualEmail = () => {
    const email = manualEmailInput.trim().toLowerCase();
    if (!email) return;

    if (!isValidEmail(email)) {
      alert('Veuillez entrer une adresse email valide');
      return;
    }

    if (manualEmails.includes(email)) {
      alert('Cette adresse email est déjà dans la liste');
      return;
    }

    setManualEmails(prev => [...prev, email]);
    setManualEmailInput('');
  };

  // Supprimer un email manuel
  const removeManualEmail = (email) => {
    setManualEmails(manualEmails.filter(e => e !== email));
  };

  // Ajouter email sur appui Entrée
  const handleManualEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addManualEmail();
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();

    if (!emailData.subject.trim() || !emailData.message.trim()) {
      alert('Veuillez remplir le sujet et le message');
      return;
    }

    if (manualOnly) {
      if (manualEmails.length === 0) {
        alert('Veuillez ajouter au moins une adresse email');
        return;
      }
    } else if (!sendToAll && selectedClients.length === 0 && manualEmails.length === 0) {
      alert('Veuillez sélectionner au moins un destinataire, ajouter une adresse email manuelle ou cocher "Envoyer à tous"');
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();

      formData.append('subject', emailData.subject);
      formData.append('message', emailData.message);
      formData.append('sendToAll', sendToAll);

      if (!sendToAll) {
        formData.append('recipients', JSON.stringify(selectedClients));
      }

      // Ajouter les adresses email manuelles
      if (manualEmails.length > 0) {
        formData.append('manualEmails', JSON.stringify(manualEmails));
      }

      // Ajouter les pièces jointes
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      let endpoint;
      if (manualOnly || isCommercial) {
        endpoint = '/api/agent/send-manual-email';
      } else if (isEshopOwner) {
        endpoint = '/api/eshop/send-email';
      } else {
        endpoint = '/api/admin/send-email';
      }
      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setResult({
        success: true,
        message: response.data.message,
        stats: response.data.stats,
        errors: response.data.errors
      });

      // Réinitialiser le formulaire
      setEmailData({
        subject: '',
        message: ''
      });
      setAttachments([]);
      setSelectedClients([]);
      setSendToAll(false);
      setNeedsConfig(false);
      setManualEmails([]);
      setManualEmailInput('');

    } catch (error) {
      console.error('Erreur lors de l\'envoi des emails:', error);

      // Vérifier si c'est une erreur de configuration manquante
      if (error.response?.status === 403 && error.response?.data?.needsConfig) {
        setNeedsConfig(true);
        setResult({
          success: false,
          message: error.response.data.message,
          needsConfig: true
        });
      } else {
        setResult({
          success: false,
          message: `Erreur: ${error.response?.data?.error || error.message}`
        });
      }
    } finally {
      setSending(false);
    }
  };

  // Filtrer les clients selon le terme de recherche
  const filteredClients = clients.filter(client =>
    client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.entreprise && client.entreprise.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="email-center">
      <div className="email-center-header">
        <h2><Mail size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Centre d'envoi d'emails</h2>
        <p>{manualOnly ? 'Envoyez des emails à des adresses personnalisées' : 'Envoyez des emails personnalisés à vos clients e-shops'}</p>
      </div>

      {/* Toggle entre Composer et Historique */}
      <div className="email-view-toggle">
        <button
          className={`toggle-btn ${activeView === 'compose' ? 'active' : ''}`}
          onClick={() => setActiveView('compose')}
        >
          <MailPlus size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Composer
        </button>
        <button
          className={`toggle-btn ${activeView === 'history' ? 'active' : ''}`}
          onClick={() => setActiveView('history')}
        >
          <ClipboardList size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Historique ({historyTotal})
        </button>
      </div>

      {activeView === 'compose' ? (
      <>
      <div className="email-form-container">
        <form onSubmit={handleSendEmail} className="email-form">

          {/* Section destinataires */}
          <div className="form-section">
            <h3><Inbox size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Destinataires</h3>

            {/* Section emails manuels */}
            <div className="manual-emails-section">
              <label>Ajouter des adresses email manuellement :</label>
              <div className="manual-email-input-container">
                <input
                  type="email"
                  placeholder="exemple@email.com"
                  value={manualEmailInput}
                  onChange={(e) => setManualEmailInput(e.target.value)}
                  onKeyPress={handleManualEmailKeyPress}
                />
                <button
                  type="button"
                  className="btn-add-email"
                  onClick={addManualEmail}
                >
                  + Ajouter
                </button>
              </div>
              {manualEmails.length > 0 && (
                <div className="manual-emails-list">
                  {manualEmails.map((email, index) => (
                    <div key={index} className="manual-email-tag">
                      <span>{email}</span>
                      <button
                        type="button"
                        className="btn-remove-email"
                        onClick={() => removeManualEmail(email)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!manualOnly && (
              <>
                <div className="separator-or">
                  <span>ou sélectionner parmi vos contacts</span>
                </div>

                <div className="send-to-all-option">
                  <label>
                    <input
                      type="checkbox"
                      checked={sendToAll}
                      onChange={(e) => {
                        setSendToAll(e.target.checked);
                        if (e.target.checked) {
                          setSelectedClients([]);
                        }
                      }}
                    />
                    <span>Envoyer à tous ({clients.filter(c => c.type === 'client').length} clients + {clients.filter(c => c.type === 'prospect').length} prospects = {clients.length} destinataires)</span>
                  </label>
                </div>
              </>
            )}

            {!manualOnly && !sendToAll && (
              <div className="clients-selector">
                <div className="clients-search">
                  <input
                    type="text"
                    placeholder="Rechercher un client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-select-all"
                    onClick={handleSelectAll}
                  >
                    {selectedClients.length === filteredClients.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                </div>

                <div className="clients-list">
                  {filteredClients.length === 0 ? (
                    <p className="no-clients">Aucun destinataire trouvé</p>
                  ) : (
                    filteredClients.map(recipient => {
                      const recipientId = `${recipient.type}-${recipient.id}`;
                      return (
                        <div key={recipientId} className="client-item">
                          <label>
                            <input
                              type="checkbox"
                              checked={selectedClients.includes(recipientId)}
                              onChange={() => handleClientSelect(recipientId)}
                            />
                            <div className="client-info">
                              <span className="client-name">
                                {recipient.nom}
                                {recipient.type === 'prospect' && (
                                  <span className="prospect-badge"><Users size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Prospect</span>
                                )}
                              </span>
                              {recipient.entreprise && (
                                <span className="client-company"> - {recipient.entreprise}</span>
                              )}
                              <span className="client-email">{recipient.email}</span>
                            </div>
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="selected-count">
                  {selectedClients.length} client(s) sélectionné(s)
                </div>
              </div>
            )}
          </div>

          {/* Section contenu de l'email */}
          <div className="form-section">
            <h3><MailPlus size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Contenu de l'email</h3>

            <div className="form-group">
              <label htmlFor="subject">Sujet *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={emailData.subject}
                onChange={handleChange}
                placeholder="Ex: Votre facture E-Shops Connect"
                maxLength="100"
                required
              />
              <small>{emailData.subject.length}/100 caractères</small>
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={emailData.message}
                onChange={handleChange}
                placeholder="Ex: Veuillez trouver ci-joint votre facture pour le mois en cours..."
                rows="8"
                required
              />
              <small>Le message sera automatiquement formaté dans un email professionnel</small>
            </div>

            <div className="form-group">
              <label htmlFor="attachments">Pièces jointes (optionnel)</label>
              <input
                type="file"
                id="attachments"
                onChange={handleFileChange}
                multiple
                accept="*/*"
              />
              <small>Maximum 10 fichiers - Formats acceptés: PDF, images, documents, etc.</small>

              {attachments.length > 0 && (
                <div className="attachments-list">
                  {attachments.map((file, index) => (
                    <div key={index} className="attachment-item">
                      <span className="attachment-name"><Paperclip size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {file.name}</span>
                      <span className="attachment-size">({(file.size / 1024).toFixed(1)} KB)</span>
                      <button
                        type="button"
                        className="btn-remove-attachment"
                        onClick={() => removeAttachment(index)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Résultat de l'envoi */}
          {result && (
            <div className={`email-result ${result.success ? 'success' : 'error'}`}>
              <p className="result-message">{result.message}</p>

              {result.stats && (
                <div className="result-stats">
                  <div className="stat">
                    <span className="stat-label">Total:</span>
                    <span className="stat-value">{result.stats.total}</span>
                  </div>
                  <div className="stat success">
                    <span className="stat-label">Envoyés:</span>
                    <span className="stat-value">{result.stats.success}</span>
                  </div>
                  {result.stats.failed > 0 && (
                    <div className="stat error">
                      <span className="stat-label">Échoués:</span>
                      <span className="stat-value">{result.stats.failed}</span>
                    </div>
                  )}
                </div>
              )}

              {result.errors && result.errors.length > 0 && (
                <div className="result-errors">
                  <p><strong>Détails des erreurs:</strong></p>
                  <ul>
                    {result.errors.map((err, index) => (
                      <li key={index}>
                        {err.client} ({err.email}): {err.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            className="btn-send-email"
            disabled={sending}
          >
            {sending ? <><Send size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoi en cours...</> : <><Mail size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoyer les emails</>}
          </button>
        </form>

        {/* Aperçu de l'email */}
        <div className="email-preview">
          <h3><Eye size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Aperçu de l'email</h3>
          <div className="email-preview-box">
            <div className="email-preview-header">
              <div className="email-preview-logo">E-Shops Connect</div>
            </div>
            <div className="email-preview-body">
              <p><strong>Bonjour [Nom du client],</strong></p>
              <div className="email-preview-message">
                {emailData.message || 'Votre message apparaîtra ici...'}
              </div>
              <p className="email-preview-signature">
                Cordialement,<br/>
                L'équipe E-Shops Connect
              </p>
            </div>
            <div className="email-preview-footer">
              © {new Date().getFullYear()} E-Shops Connect - Tous droits réservés
            </div>
          </div>
        </div>
      </div>

      <div className="email-tips">
        <h3><Lightbulb size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Conseils pour des emails efficaces</h3>
        <ul>
          <li><CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Utilisez un sujet clair et précis</li>
          <li><CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Personnalisez votre message en fonction du contexte</li>
          <li><CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Vérifiez les pièces jointes avant l'envoi</li>
          <li><CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Testez d'abord avec un seul client avant un envoi massif</li>
          <li><CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Les emails sont envoyés avec un design professionnel automatique</li>
        </ul>
      </div>
      </>
      ) : (
        /* Vue Historique */
        <div className="email-history-container">
          <div className="email-history-header">
            <h3><ClipboardList size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Historique des emails envoyés</h3>
            <button className="btn-refresh" onClick={loadEmailHistory} disabled={historyLoading}>
              <RefreshCw size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {historyLoading ? 'Chargement...' : 'Actualiser'}
            </button>
          </div>

          {historyLoading ? (
            <div className="history-loading">Chargement de l'historique...</div>
          ) : emailHistory.length === 0 ? (
            <div className="history-empty">
              <p><MailX size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Aucun email envoyé pour le moment</p>
            </div>
          ) : (
            <div className="email-history-list">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    {!manualOnly && <th>Expéditeur</th>}
                    <th>Destinataire</th>
                    <th>Sujet</th>
                    <th>Type</th>
                    <th>Statut</th>
                    <th>PJ</th>
                  </tr>
                </thead>
                <tbody>
                  {emailHistory.map((email) => (
                    <tr key={email.id} className={email.status === 'failed' ? 'row-failed' : ''}>
                      <td className="col-date">{formatDate(email.sent_at)}</td>
                      {!manualOnly && <td className="col-sender">{email.sender_display_name || email.sender_name}</td>}
                      <td className="col-recipient">
                        <div className="recipient-info">
                          <span className="recipient-email">{email.recipient_email}</span>
                          {email.recipient_name && email.recipient_name !== email.recipient_email.split('@')[0] && (
                            <span className="recipient-name">({email.recipient_name})</span>
                          )}
                        </div>
                      </td>
                      <td className="col-subject" title={email.subject}>{email.subject}</td>
                      <td className="col-type">
                        <span className={`type-badge type-${email.recipient_type}`}>
                          {email.recipient_type === 'client' ? <><User size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Client</> :
                           email.recipient_type === 'prospect' ? <><Users size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Prospect</> : <><MailPlus size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Manuel</>}
                        </span>
                      </td>
                      <td className="col-status">
                        <span className={`status-badge status-${email.status}`}>
                          {email.status === 'sent' ? <><CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoyé</> : <><XCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Échec</>}
                        </span>
                      </td>
                      <td className="col-attachments">
                        {email.has_attachments ? (
                          <span title={email.attachment_names}><Paperclip size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /></span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="history-stats">
            <span>Total: {historyTotal} email(s)</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailCenter;
